import { useMemo } from "react";
import { Text } from "../../../../components/common/typography";

interface Props {
  data: Array<{ weekStart: string; calls: number; costUSD: number }>;
}

/**
 * Small inline bar chart for the 12-week cost histogram. Pure SVG, no chart
 * library — the project hasn't standardized on one yet. If we add more
 * dashboards we should pick one (recharts / nivo / visx) and migrate.
 */
export function AiExtractionWeeklyChart({ data }: Props) {
  const maxCost = useMemo(() => Math.max(0.0001, ...data.map((d) => d.costUSD)), [data]);

  if (data.length === 0) {
    return <Text variant="bodySm" tone="muted">No activity in the last 12 weeks.</Text>;
  }

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex h-32 items-stretch gap-2">
        {data.map((bucket) => {
          const heightPct = (bucket.costUSD / maxCost) * 100;
          const label = `${formatShortDate(bucket.weekStart)}: $${bucket.costUSD.toFixed(4)} · ${bucket.calls} calls`;
          return (
            <div key={bucket.weekStart} className="group relative flex flex-1 flex-col items-center justify-end">
              <div
                className="w-full rounded-t-md bg-accentCyan700 transition-colors group-hover:bg-accentCyan"
                style={{ height: `${Math.max(heightPct, bucket.costUSD > 0 ? 4 : 0)}%` }}
                title={label}
              />
              <div className="pointer-events-none absolute -top-7 hidden whitespace-nowrap rounded-md bg-textDark px-2 py-1 group-hover:block">
                <Text variant="bodyXs" tone="inherit" className="!text-textOnDark">
                  ${bucket.costUSD.toFixed(4)} · {bucket.calls} call{bucket.calls === 1 ? "" : "s"}
                </Text>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-2">
        {data.map((bucket) => (
          <div key={`${bucket.weekStart}-label`} className="flex-1 text-center">
            <Text variant="bodyXs" tone="muted" inline>{formatShortDate(bucket.weekStart)}</Text>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleString(undefined, { month: "short" })} ${d.getDate()}`;
}
